import React from 'react';
import { Drawer, Descriptions, Tag, Typography, Space, Divider, Button } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { MatchActions } from '../../types/index.js';

const { Title, Text } = Typography;

const DetailDrawer = ({ 
  visible, 
  onClose, 
  matchResult = null,
  title = 'Match Details',
  width = 640 
}) => {
  if (!matchResult) {
    return (
      <Drawer
        title={title}
        placement="right"
        onClose={onClose}
        open={visible}
        width={width}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text type="secondary">No match result selected</Text>
        </div>
      </Drawer>
    );
  }

  const { hubspotRecord, wellskyRecord, criteria, score, action, investigationReason, originalMatch } = matchResult;

  // Safely extract criteria from the original match or create default structure
  const safeCriteria = criteria || {
    idMatch: false,
    nameMatch: false,
    phoneMatch: false,
    emailMatch: false,
    addressMatch: false
  };

  // Extract score - try multiple sources
  const matchScore = score || (originalMatch && originalMatch.matchScore) || 0;

  // If we have originalMatch data, try to extract criteria from it
  if (originalMatch && originalMatch.wellskyRecord) {
    const matchedCriteria = originalMatch.matchedCriterion || originalMatch.matchedCriteria || [];
    const criteriaArray = Array.isArray(matchedCriteria) ? matchedCriteria : [matchedCriteria];
    
    safeCriteria.idMatch = criteriaArray.includes('Database ID') || criteriaArray.includes('ID Match');
    safeCriteria.nameMatch = criteriaArray.includes('Name') || criteriaArray.includes('Full Name');
    safeCriteria.phoneMatch = criteriaArray.includes('Phone');
    safeCriteria.emailMatch = criteriaArray.includes('Email');
    safeCriteria.addressMatch = criteriaArray.includes('Address');
  }

  const getActionColor = (action) => {
    switch (action) {
      case MatchActions.READY:
        return 'green';
      case MatchActions.INVESTIGATION:
        return 'orange';
      case MatchActions.NOT_ELIGIBLE:
        return 'red';
      default:
        return 'default';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case MatchActions.READY:
        return <CheckCircleOutlined />;
      case MatchActions.INVESTIGATION:
        return <QuestionCircleOutlined />;
      case MatchActions.NOT_ELIGIBLE:
        return <CloseCircleOutlined />;
      default:
        return null;
    }
  };

  const getCriteriaIcon = (match) => {
    return match ? (
      <CheckCircleOutlined style={{ color: 'green' }} />
    ) : (
      <CloseCircleOutlined style={{ color: 'red' }} />
    );
  };

  return (
    <Drawer
      title={title}
      placement="right"
      onClose={onClose}
      open={visible}
      width={width}
    >
      {/* Match Summary */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>Match Summary</Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>Score: </Text>
            <Tag color={matchScore >= 50 ? 'green' : matchScore >= 30 ? 'orange' : 'red'}>
              {matchScore}/125 ({Math.round((matchScore / 125) * 100)}%)
            </Tag>
          </div>
          <div>
            <Text strong>Action: </Text>
            <Tag color={getActionColor(action)} icon={getActionIcon(action)}>
              {action}
            </Tag>
          </div>
          {investigationReason && (
            <div>
              <Text strong>Reason: </Text>
              <Text type="secondary">{investigationReason}</Text>
            </div>
          )}
        </Space>
      </div>

      <Divider />

      {/* Match Criteria */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>Match Criteria</Title>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item 
            label={<span>Database ID Match</span>}
          >
            <Space>
              {getCriteriaIcon(safeCriteria.idMatch)}
              <Text>{safeCriteria.idMatch ? 'Match' : 'No Match'}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Full Name Match">
            <Space>
              {getCriteriaIcon(safeCriteria.nameMatch)}
              <Text>{safeCriteria.nameMatch ? 'Match' : 'No Match'}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Phone Number Match">
            <Space>
              {getCriteriaIcon(safeCriteria.phoneMatch)}
              <Text>{safeCriteria.phoneMatch ? 'Match' : 'No Match'}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Email Match">
            <Space>
              {getCriteriaIcon(safeCriteria.emailMatch)}
              <Text>{safeCriteria.emailMatch ? 'Match' : 'No Match'}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Address Match">
            <Space>
              {getCriteriaIcon(safeCriteria.addressMatch)}
              <Text>{safeCriteria.addressMatch ? 'Match' : 'No Match'}</Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </div>

      <Divider />

      {/* HubSpot Record */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>HubSpot Record</Title>
        {hubspotRecord ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Record ID">{hubspotRecord.recordId}</Descriptions.Item>
            <Descriptions.Item label="Deal Name">{hubspotRecord.dealName}</Descriptions.Item>
            <Descriptions.Item label="HubSpot Deal Link">
              {hubspotRecord.recordId ? (
                <Button 
                  type="link" 
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => window.open(`https://app.hubspot.com/contacts/8506885/deal/${hubspotRecord.recordId}`, '_blank')}
                >
                  View in HubSpot
                </Button>
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Full Name">{hubspotRecord.fullName}</Descriptions.Item>
            <Descriptions.Item label="Client Name">{hubspotRecord.clientName || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Phone (Client)">{hubspotRecord.clientPhone || hubspotRecord.phoneClient || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Phone (Rep)">{hubspotRecord.responsiblePartyPhone || hubspotRecord.phoneRep || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Email (Client)">{hubspotRecord.clientEmail || hubspotRecord.emailClient || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Email (Rep)">{hubspotRecord.responsiblePartyEmail || hubspotRecord.emailRep || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Close Date">{hubspotRecord.closeDate || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Deal Stage">{hubspotRecord.dealStage || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Pipeline">{hubspotRecord.pipeline || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Sales Rep">{hubspotRecord.insideSalesRep || hubspotRecord.salesRep || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Commission Eligible">
              <Tag color={hubspotRecord.commissionEligible ? 'green' : 'red'}>
                {hubspotRecord.commissionEligible ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Client Type">{hubspotRecord.clientType || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="WellSky Database ID">{hubspotRecord.wellskyDatabaseId || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Assessment Address">
              {hubspotRecord.assessment ? 
                `${hubspotRecord.assessment.number || ''} ${hubspotRecord.assessment.street || ''}, ${hubspotRecord.assessment.city || ''}, ${hubspotRecord.assessment.state || ''} ${hubspotRecord.assessment.postal || ''}`.trim() 
                : 'N/A'
              }
            </Descriptions.Item>
            <Descriptions.Item label="Service Address">
              {hubspotRecord.service ? 
                `${hubspotRecord.service.number || ''} ${hubspotRecord.service.street || ''}, ${hubspotRecord.service.city || ''}, ${hubspotRecord.service.state || ''} ${hubspotRecord.service.postal || ''}`.trim() 
                : 'N/A'
              }
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary">No HubSpot record data</Text>
        )}
      </div>

      <Divider />

      {/* WellSky Record */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>WellSky Record</Title>
        {wellskyRecord ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Database ID">{wellskyRecord.databaseId}</Descriptions.Item>
            <Descriptions.Item label="Full Name">{wellskyRecord.fullName}</Descriptions.Item>
            <Descriptions.Item label="Mobile Phone">{wellskyRecord.mobilePhone || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Home Phone">{wellskyRecord.homePhone || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Email">{wellskyRecord.email || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Address">
              {wellskyRecord.streetNumber} {wellskyRecord.address}
            </Descriptions.Item>
            <Descriptions.Item label="Postal Code">{wellskyRecord.postalCode || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Agency Subdomain">{wellskyRecord.agencySubdomain || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Start Date">{wellskyRecord.startDate || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Deactivated">
              <Tag color={wellskyRecord.deactivated ? 'red' : 'green'}>
                {wellskyRecord.deactivated ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Date Deactivated">{wellskyRecord.dateDeactivated || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Spouse Name">{wellskyRecord.spouseName || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Client Referrer">{wellskyRecord.clientReferrer || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Marketer">{wellskyRecord.marketer || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Referral Source Type">{wellskyRecord.referralSourceType || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Flagged for Review">
              <Tag color={wellskyRecord.flaggedForReview ? 'orange' : 'green'}>
                {wellskyRecord.flaggedForReview ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Text type="secondary">No WellSky record matched - This is a HubSpot-only record</Text>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div style={{ textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
        <Button onClick={onClose}>Close</Button>
      </div>
    </Drawer>
  );
};

export default DetailDrawer;
